import { __decorate } from "tslib";
import { html, css, LitElement, nothing, } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property, state, query } from 'lit/decorators.js';
import { Size } from './enums/size.js';
import { InputVariant } from './enums/input-variant.js';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { Medium, ...TextfieldSize } = Size;
export const EMPTY_PLACEHOLDER = ' ';
export { InputVariant, TextfieldSize, Size };
const renderIcon = (name, partName) => {
    if (!name) {
        return '';
    }
    return html `<viedoc-icon
    .name="${name}"
    part=${partName}
    class=${partName}
  ></viedoc-icon>`;
};
let ViedocTextfield = class ViedocTextfield extends LitElement {
    constructor() {
        super();
        this.label = '';
        this.size = Size.Medium;
        this.helptext = undefined;
        this.loading = false;
        this.variant = InputVariant.Outline;
        this.prefixIcon = '';
        this.suffixIcon = '';
        this._inputElement = null;
        this._isActive = false;
        this._isDisabled = false;
        this._showSpinner = false;
        this._handleMutation = (mutations) => {
            mutations.forEach(mutation => {
                // Update disabled state if the input element disabled or readonly attribute has changed
                if (mutation.type === 'attributes' &&
                    mutation.target === this._inputElement &&
                    mutation.attributeName &&
                    ['disabled', 'readonly'].includes(mutation.attributeName)) {
                    this._setDisabledStateFromInput();
                }
            });
        };
        this._onInputKeypress = (evt) => {
            var _a;
            if (evt.key.toLowerCase() === 'enter') {
                (_a = this.closest('form')) === null || _a === void 0 ? void 0 : _a.requestSubmit();
            }
        };
        this._onInputFocus = () => {
            this._isActive = true;
        };
        this._onInputBlur = (evt) => {
            this._isActive = evt.target.value !== '';
        };
        // Get first input type element in shadow root slot
        this._getInputElement = () => {
            const inputSlot = this.shadowRoot.querySelector('slot');
            return inputSlot === null || inputSlot === void 0 ? void 0 : inputSlot.assignedElements().find(element => element.tagName.toLowerCase() === 'input');
        };
        this._updateLoadingIndicator = () => {
            if (this.loading) {
                this._showSpinner = true;
            }
            const animation = this._spinner.animate([{ opacity: 0 }, { opacity: 1 }], {
                duration: 500,
                fill: 'forwards',
                direction: this.loading ? 'normal' : 'reverse',
            });
            if (!this.loading) {
                animation.onfinish = () => {
                    this._showSpinner = false;
                };
            }
        };
        this._observer = new MutationObserver(this._handleMutation);
    }
    _setDisabledStateFromInput() {
        var _a, _b;
        this._isDisabled =
            ((_a = this._inputElement) === null || _a === void 0 ? void 0 : _a.disabled) || ((_b = this._inputElement) === null || _b === void 0 ? void 0 : _b.readOnly) || false;
    }
    _initializeInputAttributeObserver() {
        if (this._inputElement instanceof HTMLInputElement) {
            this._observer.observe(this._inputElement, {
                attributes: true,
                attributeFilter: ['disabled', 'readonly'],
            });
        }
    }
    connectedCallback() {
        super.connectedCallback();
        const input = this.querySelector('input');
        if (input) {
            this._isActive = input.value !== '' || document.activeElement === input;
        }
    }
    firstUpdated() {
        this._inputElement = this._getInputElement();
        if (this._inputElement) {
            this._setDisabledStateFromInput();
            this._initializeInputAttributeObserver();
            if (!this._inputElement.placeholder) {
                this._inputElement.placeholder = EMPTY_PLACEHOLDER;
            }
            // Note: No need to remove internal event listeners
            // https://lit.dev/docs/components/lifecycle/#disconnectedcallback
            this._inputElement.addEventListener('focus', this._onInputFocus);
            this._inputElement.addEventListener('blur', this._onInputBlur);
            this._inputElement.addEventListener('keypress', this._onInputKeypress);
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('loading')) {
            this._updateLoadingIndicator();
        }
    }
    render() {
        const wrapperClassNames = classMap({
            textfield: true,
            [`textfield--${this.size}`]: true,
            [`textfield--${this.variant}`]: true,
            'textfield--active': this._isActive && this.variant === InputVariant.Standard,
            'textfield--has-prefix': this.prefixIcon,
            'textfield--has-suffix': this.suffixIcon,
            'textfield--show-spinner': this._showSpinner,
            'textfield--disabled': this._isDisabled,
        });
        return html `
      <div class=${wrapperClassNames}>
        <label class="textfield__label">${this.label}</label>
        ${this.helptext && this.variant !== InputVariant.Standard
            ? html `<span class="textfield__help-text">${this.helptext}</span>`
            : html ``}
        <div class="textfield__input-wrapper">
          ${renderIcon(this.prefixIcon, 'prefix-icon')}
          <slot></slot>
          <viedoc-spinner></viedoc-spinner>
          ${this._showSpinner
            ? nothing
            : renderIcon(this.suffixIcon, 'suffix-icon')}
        </div>
        ${this.helptext && this.variant === InputVariant.Standard
            ? html `<span class="textfield__help-text">${this.helptext}</span>`
            : html ``}
      </div>
    `;
    }
};
ViedocTextfield.styles = [
    css `
      .textfield {
        --icon-size: 1.5rem;
        --icon-margin: 0.375rem;
        --input-font-size: var(--font-size-sm);
        --label-font-size: var(--font-size-md);
        --label-line-height: var(--line-height-normal);
        --input-padding-right: 0rem;
        --input-padding-left: 0rem;

        font-family: var(--font-family-monument);
        -webkit-font-smoothing: antialiased;
      }

      .textfield--small {
        --icon-size: 1.313rem;
        --input-font-size: var(--font-size-xs);
        --label-font-size: var(--font-size-sm);
      }

      .textfield--large {
        font-family: var(--font-family-viedoc);

        --icon-size: 1.75rem;
        --input-font-size: var(--font-size-md);
        --label-font-size: var(--font-size-xl);
      }

      .textfield__label {
        display: block;
        color: var(--primary-text-color);
        pointer-events: none;
        font-family: inherit;
        font-size: var(--label-font-size);
        line-height: var(--label-line-height);
        letter-spacing: -0.01em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .textfield__input-wrapper {
        box-sizing: border-box;
        position: relative;
        overflow: hidden; // Fixes the focus outline in Safari so that the spinner doesn't break the outline
        color: var(--primary-text-color);
      }

      ::slotted(input) {
        box-sizing: border-box;
        min-width: 0;
        width: 100%;
        height: 100%;
        font-family: inherit;
        font-size: var(--input-font-size);
        -webkit-font-smoothing: antialiased;
        margin: 0 auto;
        border: none;
        outline: none;
        padding-block: 0;
        padding-inline-start: var(--input-padding-left);
        padding-inline-end: var(--input-padding-right);
        background-color: transparent;
        color: inherit;
      }

      ::slotted(input)::placeholder {
        color: var(--secondary-text-color);
      }

      .textfield--disabled {
        color: var(--color-gray-60);
      }

      .textfield--disabled ::slotted(input) {
        -webkit-text-fill-color: var(
          --color-gray-60
        ) !important; // Fixes disabled input text color in Safari
      }

      .textfield__help-text {
        display: block;
        font-family: var(--font-family-monument);
        font-size: var(--font-size-xs);
        color: var(--color-gray-50);
        margin-bottom: 0.3125rem;
      }

      viedoc-icon,
      viedoc-spinner {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: var(--icon-size);
        height: var(--icon-size);
      }

      .prefix-icon {
        inset-inline-start: var(--input-padding-left);
      }

      .suffix-icon,
      viedoc-spinner {
        inset-inline-end: var(--input-padding-right);
      }

      viedoc-spinner {
        display: none;
        opacity: 0;
      }

      .textfield--show-spinner viedoc-spinner {
        display: block;
      }

      .textfield--has-suffix ::slotted(input),
      .textfield--show-spinner ::slotted(input) {
        padding-inline-end: calc(
          var(--input-padding-right) + var(--icon-size) + var(--icon-margin)
        );
      }

      .textfield--has-prefix ::slotted(input) {
        padding-inline-start: calc(
          var(--input-padding-left) + var(--icon-size) + var(--icon-margin)
        );
      }

      /* Outline variant */

      .textfield--outline {
        --input-padding-left: 0.875rem;
        --input-padding-right: 1rem;
      }

      .textfield--outline .textfield__input-wrapper {
        background-color: var(--secondary-background-color);
        border: 1px solid var(--color-gray-30);
        border-radius: 0.625rem;
        height: 2.563rem;
        margin-top: 0.625rem;
      }

      .textfield--outline .textfield__input-wrapper:focus-within {
        outline: 5px auto Highlight;
        outline: 5px auto -webkit-focus-ring-color;
      }

      .textfield--outline.textfield--small .textfield__input-wrapper {
        height: 2.25rem;
        margin-top: 0.375rem;
      }

      .textfield--outline.textfield--large .textfield__input-wrapper {
        height: 3rem;
      }

      .textfield--outline.textfield--disabled .textfield__input-wrapper {
        background: var(--color-gray-10);
        border: none;
      }

      .textfield--outline .textfield__label:empty ~ .textfield__input-wrapper {
        margin-top: 0;
      }

      /* Standard variant */

      .textfield--standard {
        --icon-margin: 0.625rem;
        --label-font-size: var(--font-size-2xs);

        position: relative;
        /* Space for the label when it is floating (active state) */
        padding-top: calc(var(--label-font-size) * var(--label-line-height));
      }

      .textfield--standard.textfield--large {
        --input-font-size: var(--font-size-2xl);
        --label-font-size: var(--font-size-sm);
      }

      .textfield--standard.textfield--small {
        --label-font-size: var(--font-size-3xs);
      }

      .textfield--standard .textfield__input-wrapper {
        border-bottom: solid 1px var(--color-gray-20);
      }

      .textfield--standard ::slotted(input),
      .textfield--standard viedoc-icon,
      .textfield--standard viedoc-spinner {
        padding-bottom: 0.375rem;
      }

      .textfield--standard.textfield--active .textfield__input-wrapper {
        border-color: var(--color-gray-100);
      }

      .textfield--standard.textfield--disabled .textfield__input-wrapper {
        border-color: var(--color-gray-30);
      }

      .textfield--standard.textfield--disabled {
        color: var(--color-gray-40);
      }
      .textfield--standard.textfield--disabled ::slotted(input) {
        -webkit-text-fill-color: var(
          --color-gray-40
        ) !important; // Fixes disabled input text color in Safari
      }

      .textfield--standard ::slotted(input)::placeholder {
        opacity: 0;
        transition: opacity 200ms;
      }

      .textfield--active ::slotted(input)::placeholder {
        opacity: 1;
      }

      .textfield--standard .textfield__label {
        position: absolute;
        top: 0;
        color: var(--secondary-text-color);
        transition:
          transform 200ms,
          color 200ms,
          font-size 200ms;
      }

      .textfield--standard.textfield--active .textfield__label {
        color: var(--primary-text-color);
      }

      .textfield--standard.textfield--disabled .textfield__label {
        color: var(--color-gray-40);
      }

      .textfield--standard.textfield--has-prefix .textfield__label {
        margin-inline-start: calc(var(--icon-size) + var(--icon-margin));
      }

      .textfield--standard:not(.textfield--active) .textfield__label {
        font-size: var(--input-font-size);
        transform: translateY(1.1rem);
      }

      .textfield--standard.textfield--small:not(.textfield--active)
        .textfield__label {
        transform: translateY(1rem);
      }
      .textfield--standard.textfield--large:not(.textfield--active)
        .textfield__label {
        transform: translateY(1.5rem);
      }

      .textfield--standard .textfield__help-text {
        margin-top: 0.4rem;
      }
    `,
];
__decorate([
    property({ type: String })
], ViedocTextfield.prototype, "label", void 0);
__decorate([
    property({ type: String })
], ViedocTextfield.prototype, "size", void 0);
__decorate([
    property({ type: String, attribute: 'help-text' })
], ViedocTextfield.prototype, "helptext", void 0);
__decorate([
    property({ type: Boolean })
], ViedocTextfield.prototype, "loading", void 0);
__decorate([
    property({ type: String })
], ViedocTextfield.prototype, "variant", void 0);
__decorate([
    property({ type: String, attribute: 'prefix-icon' })
], ViedocTextfield.prototype, "prefixIcon", void 0);
__decorate([
    property({ type: String, attribute: 'suffix-icon' })
], ViedocTextfield.prototype, "suffixIcon", void 0);
__decorate([
    query('viedoc-spinner')
], ViedocTextfield.prototype, "_spinner", void 0);
__decorate([
    state()
], ViedocTextfield.prototype, "_isActive", void 0);
__decorate([
    state()
], ViedocTextfield.prototype, "_isDisabled", void 0);
__decorate([
    state()
], ViedocTextfield.prototype, "_showSpinner", void 0);
ViedocTextfield = __decorate([
    customElement('viedoc-textfield')
], ViedocTextfield);
export { ViedocTextfield };
